import { CartPersistenceError } from "../types/cart.types";

const CART_LOCK_NAME = "ecommerce:shopping_cart:mutation";
const CART_LOCK_DB_NAME = "ecommerce_cart_coordination";
const CART_LOCK_DB_VERSION = 1;
const CART_LOCK_STORE_NAME = "mutex";

let databasePromise: Promise<IDBDatabase> | null = null;

const openLockDatabase = (): Promise<IDBDatabase> => {
    if (databasePromise) return databasePromise;

    databasePromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(
                new CartPersistenceError(
                    "LOCK_UNAVAILABLE",
                    "Web Locks e IndexedDB no están disponibles para coordinar el carrito.",
                ),
            );
            return;
        }

        const request = indexedDB.open(CART_LOCK_DB_NAME, CART_LOCK_DB_VERSION);

        request.onupgradeneeded = () => {
            const database = request.result;
            if (!database.objectStoreNames.contains(CART_LOCK_STORE_NAME)) {
                database.createObjectStore(CART_LOCK_STORE_NAME, { keyPath: "name" });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
            databasePromise = null;
            reject(
                new CartPersistenceError(
                    "LOCK_UNAVAILABLE",
                    "No se pudo abrir la coordinación IndexedDB del carrito.",
                    request.error,
                ),
            );
        };
        request.onblocked = () => {
            databasePromise = null;
            reject(
                new CartPersistenceError(
                    "LOCK_UNAVAILABLE",
                    "La coordinación IndexedDB del carrito quedó bloqueada.",
                ),
            );
        };
    });

    return databasePromise;
};

const runWithIndexedDbLock = async <T>(work: () => T): Promise<T> => {
    const database = await openLockDatabase();

    return new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(CART_LOCK_STORE_NAME, "readwrite");
        const store = transaction.objectStore(CART_LOCK_STORE_NAME);
        const request = store.get(CART_LOCK_NAME);
        let workExecuted = false;
        let workResult: T;
        let workError: unknown;

        request.onsuccess = () => {
            try {
                workResult = work();
                workExecuted = true;
            } catch (error) {
                workError = error;
                transaction.abort();
                return;
            }

            try {
                store.put({ name: CART_LOCK_NAME, updatedAt: Date.now() });
            } catch {
                transaction.abort();
            }
        };

        request.onerror = () => {
            workError = request.error;
            transaction.abort();
        };

        transaction.oncomplete = () => resolve(workResult);
        transaction.onabort = () => {
            if (workExecuted && workError === undefined) {
                console.warn(
                    "La transacción mutex del carrito se abortó después de confirmar localStorage.",
                );
                resolve(workResult);
                return;
            }

            if (workError instanceof CartPersistenceError) {
                reject(workError);
                return;
            }

            reject(
                new CartPersistenceError(
                    "LOCK_FAILED",
                    "Falló la sección crítica IndexedDB del carrito.",
                    workError ?? transaction.error,
                ),
            );
        };
        transaction.onerror = () => {
            // El resultado final se resuelve en oncomplete/onabort.
        };
    });
};

const hasWebLocks = (): boolean =>
    typeof navigator !== "undefined" &&
    "locks" in navigator &&
    typeof navigator.locks?.request === "function";

export const runWithCartLock = async <T>(work: () => T): Promise<T> => {
    if (!hasWebLocks()) return runWithIndexedDbLock(work);

    try {
        return await navigator.locks.request(CART_LOCK_NAME, { mode: "exclusive" }, () => work());
    } catch (error) {
        if (error instanceof CartPersistenceError) throw error;

        throw new CartPersistenceError(
            "LOCK_FAILED",
            "Falló el lock exclusivo del carrito.",
            error,
        );
    }
};
